import { useState } from 'react';
import { Alert, Button, Card, EmptyState, Field, Input, Skeleton } from '@/shared/ui';
import { formatClp } from '@/shared/lib/format';
import { errorMessage } from '@/shared/api/client';
import { useToast } from '@/app/providers';
import { RequestCard } from '@/features/fxr/RequestCard';
import { type ManagerException, useDecideException, useExceptions } from '@/features/fxr/api';

/** Gerencia → Excepciones: aprobar/rechazar solicitudes que superan el límite o con rendición vencida. */
export function ExceptionsPage() {
  const [tab, setTab] = useState<'PENDIENTE' | 'APROBADA' | 'RECHAZADA'>('PENDIENTE');
  const q = useExceptions(tab);
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-5)', flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
        <h1>Excepciones</h1>
        <div style={{ display: 'flex', gap: 4 }}>{(['PENDIENTE', 'APROBADA', 'RECHAZADA'] as const).map((t) => <Button key={t} size="sm" variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)}>{t === 'PENDIENTE' ? 'Pendientes' : t === 'APROBADA' ? 'Aprobadas' : 'Rechazadas'}</Button>)}</div>
      </div>
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        {q.isPending && <Skeleton height={120} />}
        {q.data && q.data.length === 0 && <EmptyState title="Sin excepciones" description="Se derivan aquí las solicitudes cuya exposición proyectada supera el límite individual o cuyo beneficiario tiene una rendición vencida (§6.1, §20.4)." />}
        {q.data?.map((e) => <Row key={e.id} e={e} />)}
      </div>
    </>
  );
}

function Row({ e }: { e: ManagerException }) {
  const decide = useDecideException();
  const toast = useToast();
  const [note, setNote] = useState('');
  const r = e.request;
  const limit = e.reasons.find((x) => x.kind === 'LIMITE_EXPOSICION');
  return (
    <div>
      <RequestCard r={r} />
      {e.status === 'PENDIENTE' && (
        <Card>
          {limit && <Alert kind="warning">Exposición proyectada <b className="num">{formatClp(limit.projected ?? 0)}</b> supera el límite <b className="num">{formatClp(limit.limit ?? 0)}</b> en <b className="num">{formatClp(limit.excess ?? 0)}</b>. No se rechaza automáticamente: la decisión es de Gerencia.</Alert>}
          {e.reasons.some((x) => x.kind === 'RENDICION_VENCIDA') && <Alert kind="warning">El beneficiario tiene una rendición vencida: la nueva asignación requiere autorización (§20.4).</Alert>}
          <Field label="Nota (obligatoria para rechazar)" htmlFor={`n-${e.id}`}><Input id={`n-${e.id}`} value={note} onChange={(ev) => setNote(ev.target.value)} maxLength={1000} /></Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button loading={decide.isPending} onClick={() => decide.mutate({ id: e.id, version: e.version, approve: true, note: note || undefined }, { onSuccess: () => toast.notify({ title: 'Excepción aprobada', description: 'Finanzas fue notificada para procesar la solicitud.' }), onError: (er) => toast.notify({ title: 'No se pudo aprobar', description: errorMessage(er), kind: 'error' }) })}>Aprobar excepción</Button>
            <Button variant="danger" loading={decide.isPending} disabled={note.trim().length < 3} onClick={() => decide.mutate({ id: e.id, version: e.version, approve: false, note }, { onSuccess: () => toast.notify({ title: 'Excepción rechazada' }), onError: (er) => toast.notify({ title: 'No se pudo rechazar', description: errorMessage(er), kind: 'error' }) })}>Rechazar</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
